import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, ShieldOff, Trash2 } from "lucide-react";

export default function Admin() {
  const { profiles } = useProfiles();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/approve`);
      toast.success("User approved");
      load();
    } catch {
      toast.error("Failed to approve user");
    }
  };

  const revoke = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/revoke`);
      toast.success("Access revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke access");
    }
  };

  const removeUser = async (userId) => {
    if (!window.confirm("Permanently remove this user?")) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("User removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove user");
    }
  };

  const toggleProfileAccess = async (u, profileId) => {
    const current = u.allowed_profile_ids || [];
    const next = current.includes(profileId)
      ? current.filter((id) => id !== profileId)
      : [...current, profileId];
    try {
      await api.put(`/admin/users/${u.user_id}/access`, { profile_ids: next });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, allowed_profile_ids: next } : x)));
    } catch {
      toast.error("Failed to update access");
    }
  };

  const toggleDirectors = async (u) => {
    const enabled = !u.directors_access;
    try {
      await api.put(`/admin/users/${u.user_id}/directors`, { enabled });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, directors_access: enabled } : x)));
      toast.success(enabled ? "Letters section enabled" : "Letters section disabled");
    } catch {
      toast.error("Failed to update Letters access");
    }
  };

  const toggleSalary = async (u) => {
    const enabled = !u.salary_access;
    try {
      await api.put(`/admin/users/${u.user_id}/salary`, { enabled });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, salary_access: enabled } : x)));
      toast.success(enabled ? "Salary section enabled" : "Salary section disabled");
    } catch {
      toast.error("Failed to update Salary access");
    }
  };

  if (loading) {
    return <div className="p-10 text-neutral-500 font-mont text-xs uppercase tracking-[0.3em]">Loading…</div>;
  }

  return (
    <div className="p-8 max-w-5xl">
      <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">— Admin</p>
      <h1 className="font-serif text-3xl text-white mb-1">Team access</h1>
      <p className="text-neutral-500 text-sm mb-10">Approve teammates and choose which business profiles each person can see.</p>

      <div className="border border-white/10 divide-y divide-white/10">
        {users.map((u) => (
          <div key={u.user_id} className="p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-white text-sm flex items-center gap-2">
                  {u.name}
                  {u.role === "admin" && (
                    <span className="text-[9px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-1.5 py-0.5">Admin</span>
                  )}
                  {u.role !== "admin" && (
                    <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${u.is_approved ? "text-green-400 border-green-400/40" : "text-yellow-400 border-yellow-400/40"}`}>
                      {u.is_approved ? "Approved" : "Pending"}
                    </span>
                  )}
                </div>
                <div className="text-neutral-500 text-xs font-mont">{u.email}</div>
              </div>

              {u.role !== "admin" && (
                <div className="flex items-center gap-2">
                  {!u.is_approved ? (
                    <Button onClick={() => approve(u.user_id)} className="rounded-none bg-white text-black hover:bg-neutral-200 text-xs h-8 px-3">
                      <ShieldCheck className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Approve
                    </Button>
                  ) : (
                    <Button onClick={() => revoke(u.user_id)} variant="outline" className="rounded-none border-white/20 text-neutral-300 hover:text-white text-xs h-8 px-3">
                      <ShieldOff className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Revoke
                    </Button>
                  )}
                  <Button onClick={() => removeUser(u.user_id)} variant="outline" className="rounded-none border-white/20 text-neutral-400 hover:text-red-400 text-xs h-8 px-3">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                </div>
              )}
            </div>

            {u.role !== "admin" && u.is_approved && (
              <div className="pl-1">
                <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Business access</p>
                <div className="flex flex-wrap gap-4">
                  {profiles.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
                      <Checkbox
                        checked={(u.allowed_profile_ids || []).includes(p.id)}
                        onCheckedChange={() => toggleProfileAccess(u, p.id)}
                      />
                      {p.name}
                    </label>
                  ))}
                  {profiles.length === 0 && (
                    <span className="text-neutral-600 text-sm">No business profiles created yet.</span>
                  )}
                </div>
                <p className="text-[10px] uppercase tracking-widest text-neutral-500 mt-4 mb-2">Special sections</p>
                <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
                  <Checkbox
                    data-testid={`directors-access-${u.user_id}`}
                    checked={!!u.directors_access}
                    onCheckedChange={() => toggleDirectors(u)}
                  />
                  Letters section (company letters)
                </label>
                <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer mt-2">
                  <Checkbox
                    data-testid={`salary-access-${u.user_id}`}
                    checked={!!u.salary_access}
                    onCheckedChange={() => toggleSalary(u)}
                  />
                  Salary section (employee salaries, inside Finance)
                </label>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
